/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/* ==================== UTILS ==================== */

const UTILS = {
  formatDateInFrench(date) {
    const options = { month: "long", day: "numeric" };
    return date.toLocaleDateString("fr-FR", options);
  },
  generateRandomId(length) {
    let result = "";
    const characters = "0123456789";
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charactersLength);
      result += characters.charAt(randomIndex);
    }
    return result;
  },
};

/* ==================== NOTE DE COMMANDE ====================
   La note est enregistrée pendant la frappe. Auparavant, chaque « keyup »
   déclenchait un fetch indépendant et non ordonné vers cart/update.js.
   En tapant vite, la requête d'une frappe intermédiaire (« Félicitat »)
   pouvait arriver côté serveur APRÈS la requête finale (« Félicitations »)
   et l'écraser : la note se retrouvait tronquée sur un état intermédiaire.

   On sérialise donc les écritures dans une file (chaque requête attend la
   précédente) : la dernière valeur saisie est toujours la dernière écrite.
   Un debounce limite le nombre de requêtes, et un flush garantit l'envoi
   de la valeur complète à la validation / fermeture.
========================================================================= */
let noteSaveChain = Promise.resolve();
let noteSaveTimer = null;

function saveCartNote(value) {
  noteSaveChain = noteSaveChain
    .catch(() => {})
    .then(() =>
      fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: value }),
        keepalive: true,
      })
    );
  return noteSaveChain;
}

// Enregistrement pendant la frappe (temporisé).
function saveCartNoteDebounced(value) {
  clearTimeout(noteSaveTimer);
  noteSaveTimer = setTimeout(() => saveCartNote(value), 350);
}

// Enregistrement immédiat de la valeur définitive (validation, fermeture,
// suppression) : annule le debounce en attente et met la valeur en file.
function flushCartNote(value) {
  clearTimeout(noteSaveTimer);
  return saveCartNote(value);
}

// Loader du bouton d'ajout au panier. L'URL du GIF est injectée depuis le
// layout (window.THEME.loadingGif -> asset local), pour ne plus dépendre
// d'une URL codée en dur vers une ancienne version de thème.
const LOADING_HTML = `<img class="loading" src="${
  (window.THEME && window.THEME.loadingGif) || ""
}" role="presentation" alt="">`;

class ExtimatedDate extends HTMLElement {
  constructor() {
    super();

    this.days = Number(this.dataset.days);

    if (this.days == 0)
      this.querySelector(".date").innerHTML =
        "Aujourd'hui (pour les commandes passées avant 15h)";
    else {
      this.date = new Date();
      this.estimatedDay = this.calculateShippingDate(this.date, this.days);
      this.querySelector(".date").innerHTML = UTILS.formatDateInFrench(
        this.estimatedDay
      );
    }
  }

  calculateShippingDate(date, days) {
    let addedDays = 0;
    let currentDay = new Date(date.getTime());

    while (addedDays < days) {
      currentDay.setDate(currentDay.getDate() + 1);
      if (currentDay.getDay() !== 0 && currentDay.getDay() !== 6) {
        addedDays++;
      }
    }

    return currentDay;
  }
}

customElements.define("estimated-date", ExtimatedDate);

/* ==================== CART DRAWER BANNER ==================== */

class CartDrawerBanner extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // Get the required elements
    const bannerContainer = this;
    const banner = bannerContainer.querySelector(".bandeau-container");

    // Clone the banner content
    const bannerClone = banner.cloneNode(true);
    bannerContainer.appendChild(bannerClone);

    const bannerWidth = banner.clientWidth;
    let offset = 0;

    function moveBanner() {
      offset--;
      banner.style.transform = `translateX(${offset}px)`;
      bannerClone.style.transform = `translateX(${offset + bannerWidth}px)`;

      if (offset <= -bannerWidth) {
        offset = 0;
      }

      requestAnimationFrame(moveBanner);
    }

    moveBanner();
  }
}

customElements.define("cart-drawer-banner", CartDrawerBanner);

/* ==================== CART DRAWER ==================== */

const drawer = document.querySelector("cart-drawer");

/*
 * Le thème (Flow) ajoute au panier au clic sur [data-action="add-to-cart"]
 * via son propre gestionnaire, en faisant preventDefault quand le panier
 * AJAX est activé : l'événement "submit" ne se déclenche alors jamais.
 * On intercepte donc le clic en phase de capture pour prendre la main sur
 * les produits personnalisés (le thème continue de gérer les autres).
 */
document.addEventListener(
  "click",
  (e) => {
    const button = e.target.closest('[data-action="add-to-cart"]');
    if (!button) return;

    const form = button.closest('form[action*="/cart/add"]');
    if (!form || !drawer) return;

    // Produit sans personnalisation : on laisse le thème gérer normalement.
    if (!drawer.isCustom()) return;

    // Produit personnalisé : on empêche le gestionnaire du thème et on
    // ajoute nous-mêmes le produit + la variante de personnalisation.
    e.preventDefault();
    e.stopImmediatePropagation();

    // Respecte la validation native (ex. prénom requis quand "Oui").
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    drawer.addCustomProductToCart(e, form);
  },
  true
);

// Ouverture du panier
document.querySelectorAll('.Header a[href="/cart"]').forEach((a) => {
  a.addEventListener("click", (e) => {
    e.preventDefault();
    drawer.openCartDrawer();
  });
});

// Fermeture du panier
document
  .querySelectorAll(".cart-drawer__header-close, .cart-drawer")
  .forEach((el) => {
    el.addEventListener("click", () => {
      drawer.closeCartDrawer();
    });
  });

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addDynamicEventListeners();
    this.addEventListeners();
  }

  isCustom() {
    return document.querySelector(".custom-form")?.classList.contains("active");
  }

  async addProductToCart(e, form) {
    e.preventDefault();

    // On ajoute un loader
    const submitInput = form.querySelector('*[type="submit"]');
    const html = submitInput.innerHTML;
    submitInput.innerHTML = LOADING_HTML;

    // Submit form with ajax
    await fetch("/cart/add", {
      method: "post",
      body: new FormData(form),
    });

    // Get cart count
    const res = await fetch("/cart.js");
    const cart = await res.json();
    this.updateCartItemCounts(cart.item_count);

    // Update cart
    await this.updateCart();

    // On remet le texte initial en place
    submitInput.innerHTML = html;

    // Open cart drawer
    this.openCartDrawer();
  }

  // Ajoute une variante au panier via l'API AJAX de Shopify (sans jQuery/CartJS)
  async addCartItem(id, quantity, properties) {
    const res = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id,
        quantity: Number(quantity) || 1,
        properties,
      }),
    });

    if (!res.ok) {
      throw new Error(`cart/add.js a répondu ${res.status}`);
    }

    return res.json();
  }

  async addCustomProductToCart(e, form) {
    e.preventDefault();

    // On ajoute un loader
    const submitInput = form.querySelector('*[type="submit"]');
    const html = submitInput.innerHTML;
    submitInput.innerHTML = LOADING_HTML;

    // On récupère les infos de la variante à ajouter au panier
    const productID = document.querySelector(
      '.Product__Info input[name="id"]'
    ).value;

    const productTitle = document.querySelector(
      ".ProductMeta__Title"
    ).textContent;

    // On récupère le texte de la personnalisation
    const personnalisationID = document.querySelector(
      "#personnalisationID"
    ).value;

    const personnalisation = document.querySelector(".custom-form-input").value;

    // On récupère la quantité à ajouter au panier
    const quantity = document.querySelector('input[name="quantity"]').value;

    // Ref partagée pour lier le produit et sa personnalisation dans le panier
    const ref = UTILS.generateRandomId(10);

    try {
      // 1. Le produit lui-même
      await this.addCartItem(productID, quantity, { Ref: ref });

      // 2. La personnalisation (produit lié). Si l'ID est vide, on n'ajoute
      //    rien mais on ne bloque pas l'ajout du produit de base.
      if (personnalisationID) {
        await this.addCartItem(personnalisationID, quantity, {
          Produit: productTitle,
          Personnalisation: personnalisation,
          Ref: ref,
        });
      } else {
        console.warn(
          "Personnalisation : produit lié introuvable, broderie ignorée."
        );
      }
    } catch (err) {
      console.error("Ajout au panier (personnalisation) échoué :", err);
    } finally {
      // On met à jour le compteur, on rafraîchit le panier et on ouvre le drawer
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      this.updateCartItemCounts(cart.item_count);
      await this.updateCart();
      submitInput.innerHTML = html;
      this.openCartDrawer();
    }
  }

  handleAddToCartForm(e, form) {
    if (this.isCustom()) {
      this.addCustomProductToCart(e, form);
    } else {
      this.addProductToCart(e, form);
    }
  }

  addEventListeners() {
    document
      .querySelectorAll(".cart-drawer__drawer, .cart-drawer__note")
      .forEach((el) => {
        el.addEventListener("click", (e) => {
          e.stopPropagation();
        });
      });
  }

  openCartDrawer() {
    document.body.style.overflow = "hidden";
    document.querySelector(".cart-drawer").classList.add("cart-drawer--active");
  }

  closeCartDrawer() {
    document.body.style.overflow = "auto";
    document
      .querySelector(".cart-drawer")
      .classList.remove("cart-drawer--active");
  }

  openCartNote() {
    document
      .querySelector(".cart-drawer__note")
      .classList.add("cart-drawer__note--active");
  }

  closeCartNote() {
    document
      .querySelector(".cart-drawer__note")
      .classList.remove("cart-drawer__note--active");

    // On récupère la note
    const note = document.querySelector("#cart-drawer-note").value;

    // Si la note est vide, on masque le bouton d'édition
    if (note === "") {
      document.getElementById("cart-drawer-activate-note").checked = false;
      document
        .querySelector(".cart-drawer__edit-note")
        .classList.remove("cart-drawer__edit-note--active");
    } else {
      // À la fermeture, on force l'enregistrement de la valeur complète au
      // cas où un enregistrement temporisé serait encore en attente.
      flushCartNote(note.trim());
    }
  }

  updateCartItemCounts(count) {
    document.querySelectorAll(".Header__CartCount").forEach((el) => {
      el.textContent = count;
    });
  }

  async updateCart() {
    const res = await fetch("/?snippet_id=cart-drawer-flow");
    const text = await res.text();

    this.removeCartDrawerDynamicListeners();

    const html = document.createElement("div");
    html.innerHTML = text;
    const newBox = html.querySelector(".cart-drawer__flow").innerHTML;
    document.querySelector(".cart-drawer__flow").innerHTML = newBox;

    this.addDynamicEventListeners();
  }

  addDynamicEventListeners() {
    // Update quantities
    document
      .querySelectorAll(".cart-drawer__quantity-selector button")
      .forEach((button) => {
        button.addEventListener("click", () => {
          this.handleQuantityButtonClick(button);
        });
      });

    // Delete product
    document
      .querySelectorAll(".cart-drawer__item-delete-product")
      .forEach((button) => {
        button.addEventListener("click", () => {
          this.handleDeleteClick(button);
        });
      });

    const cartDrawerOpenNote = document.getElementById(
      "cart-drawer-activate-note"
    );

    const cartDrawerEditNote = document.querySelector(
      ".cart-drawer__edit-note"
    );

    // Open note
    cartDrawerOpenNote?.addEventListener("change", (el) => {
      if (el.target.checked) {
        drawer.openCartNote();
      } else {
        drawer.closeCartNote();
        cartDrawerEditNote.classList.remove("cart-drawer__edit-note--active");
        // On supprime la note (via la file, pour annuler tout
        // enregistrement temporisé encore en attente).
        flushCartNote("");
      }
    });

    cartDrawerEditNote?.addEventListener("click", () => {
      drawer.openCartNote();
    });

    // Close note
    const cartDrawerNoteClose = document.querySelector(
      ".cart-drawer__note-close"
    );

    cartDrawerNoteClose?.addEventListener("click", () => {
      drawer.closeCartNote();
    });

    document
      .getElementById("cart-drawer-note-form")
      .addEventListener("submit", (e) => {
        e.preventDefault();
        // Écrit la valeur définitive et complète, quelle que soit l'issue
        // des enregistrements temporisés en cours.
        const finalNote = document
          .getElementById("cart-drawer-note")
          .value.trim();
        if (finalNote !== "") flushCartNote(finalNote);
        drawer.closeCartNote();
        cartDrawerEditNote.classList.add("cart-drawer__edit-note--active");
      });

    const cartDrawerNote = document.getElementById("cart-drawer-note");
    const cartDrawerNoteSubmit = document.querySelector(
      ".cart-drawer__note-form button"
    );

    cartDrawerNote.addEventListener("keyup", (e) => {
      cartDrawerNoteSubmit.classList.toggle(
        "button--disabled",
        e.target.value === ""
      );

      if (e.target.value === "") return;

      // Enregistrement temporisé et sérialisé (voir saveCartNote) pour
      // éviter qu'une frappe intermédiaire n'écrase la valeur finale.
      saveCartNoteDebounced(e.target.value.trim());
    });
  }

  removeCartDrawerDynamicListeners() {
    document
      .querySelectorAll(".cart-drawer-quantity-selector button")
      .forEach((button) => {
        button.removeEventListener("click", this.handleQuantityButtonClick);
      });

    document
      .querySelectorAll(".cart-drawer-item__delete-product")
      .forEach((button) => {
        button.removeEventListener("click", this.handleDeleteClick);
      });
  }

  async handleDeleteClick(button) {
    // Get line item key
    const rootItem = button.parentElement.parentElement.parentElement;
    const key = rootItem.getAttribute("data-line-item-key");

    let body;

    body = JSON.stringify({ updates: { [key]: 0 } });

    // Ajax update\
    const res = await fetch("/cart/update.js", {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: body,
    });

    const cart = await res.json();

    this.updateCartItemCounts(cart.item_count);

    // Update cart
    this.updateCart();
  }

  async handleQuantityButtonClick(button) {
    // Get line item key
    const rootItem =
      button.parentElement.parentElement.parentElement.parentElement
        .parentElement;

    // On récupère la key du produit
    const productKey = rootItem.getAttribute("data-line-item-key");
    let personnalisationProductKey = null;

    // On vérifie si on produit personnalisation est associé
    for (let item of rootItem.parentElement.querySelectorAll(
      "[data-line-item-key]"
    )) {
      if (item.getAttribute("data-line-item-parent-key") == productKey) {
        personnalisationProductKey = item.getAttribute("data-line-item-key");
        break;
      }
    }

    // Get new quantity
    const currentQuantity = Number(
      button.parentElement.querySelector("input").value
    );
    const isUp = button.classList.contains(
      "cart-drawer__quantity-selector-plus"
    );
    const newQuantity = isUp ? currentQuantity + 1 : currentQuantity - 1;

    let body;

    if (personnalisationProductKey) {
      // Si on a un produit à synchroniser, on applique la même modification de quantité que le produit associé
      body = JSON.stringify({
        updates: {
          [productKey]: newQuantity,
          [personnalisationProductKey]: newQuantity,
        },
      });
    } else {
      body = JSON.stringify({
        updates: { [productKey]: newQuantity },
      });
    }

    // Ajax update\
    const res = await fetch("/cart/update.js", {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: body,
    });

    const cart = await res.json();

    this.updateCartItemCounts(cart.item_count);

    // Update cart
    this.updateCart();
  }
}

customElements.define("cart-drawer", CartDrawer);

/* ================== CART DRAWER UPSELL ================== */

class CartDrawerUpsell extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector(".cart-drawer__upsell-arrow--left").addEventListener(
      "click",
      () => {
        alert("left");
      }
    );

    this.querySelector(".cart-drawer__upsell-arrow--right").addEventListener(
      "click",
      () => {
        alert("right");
      }
    );
  }
}

/* ================== PRODUCT SUGGESTION WRAP ================== */

class ProductSuggestionWrap extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setSlider();
  }

  setSlider() {
    const slider = new Splide(this, {
      type: "slide",
      perPage: 3,
      gap: 25,
      arrows: true,
      pagination: false,
      breakpoints: {
        640: {
          perPage: 2,
        },
      },
    });
    slider.mount();
  }
}

customElements.define("product-suggestion-wrap", ProductSuggestionWrap);

/* ================== PRODUCT SUGGESTION ================== */

class ProductSuggestion extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.setEventListeners();
  }

  setEventListeners() {
    const form = this.querySelector("form");

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      // On ajoute un loader
      const submitInput = form.querySelector('*[type="submit"]');
      submitInput.innerHTML = LOADING_HTML;

      // Submit form with ajax
      await fetch("/cart/add", {
        method: "post",
        body: new FormData(form),
      });

      document.querySelector(".cart-drawer").updateCart();
    });
  }
}

customElements.define("product-suggestion", ProductSuggestion);

/* ================== ONE CLICK PRODUCT ================== */
class CartDrawerOneClickProduct extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.form.addEventListener("submit", async (e) => {
      e.preventDefault();
      // Submit form with ajax
      await fetch("/cart/add", {
        method: "post",
        body: new FormData(this.form),
      });
      document.querySelector(".cart-drawer").updateCart();
    });
  }

  disconnectedCallback() {
    this.form.removeEventListener("submit", this.handleSubmit);
  }
}

customElements.define(
  "cart-drawer-one-click-product",
  CartDrawerOneClickProduct
);

/* ================== PRODUCT CUSTOMIZATION ================== */

class ProductCustomizationWrap extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.currentName = "";

    this.inputID = document.querySelector(
      '.Product__Info form input[name="id"]'
    );
    this.productPrice = document.querySelector(
      ".ProductMeta__Price:not(.Price--compareAt)"
    );
    this.productPricePrecommande = document.querySelector(
      ".product-meta__price-item--precommande"
    );
    this.productATC = document.querySelector(".ProductForm__AddToCart");

    this.setEventListeners();
  }

  updatePrice() {
    this.productPrice.textContent = this.price;
    this.productATC.querySelector("span:last-of-type").textContent = this.price;
  }

  setEventListeners() {
    const customForm = this.querySelector(".custom-form");
    const customFormInput = this.querySelector(".custom-form-input");
    const customizationForm = this.querySelector("input#customNameInput");

    for (let choice of this.querySelectorAll(".product-customization-choice")) {
      choice.addEventListener("change", (e) => {
        this.price = e.target.dataset.price;
        this.updatePrice();

        const state = e.target.dataset.state;

        if (state === "oui") {
          // On affiche le formulaire
          customForm.classList.add("active");
          this.productPricePrecommande?.classList.add("active");
          customFormInput.required = true;

          // On affiche le nom gardé en mémoire
          customizationForm.value = this.currentName;
        } else if (state === "non") {
          // On cache le formulaire
          customForm.classList.remove("active");
          this.productPricePrecommande?.classList.remove("active");
          customFormInput.required = false;

          // On garde le nom en mémoire
          this.currentName = customizationForm.value;

          // On efface le nom pour ne pas qu'il s'ajoute au panier
          customizationForm.value = "";
        }
      });
    }
  }
}

customElements.define("product-customization-wrap", ProductCustomizationWrap);

/* ================== PREORDER COUNTER ================== */

class EditoCounter extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.countdownDate = new Date(
      this.getAttribute("data-estimated_date")
    ).getTime();
    this.setCounter();
    this.classList.add("edito-counter__counter--active");
  }

  updateCountdown() {
    const now = new Date().getTime();
    const difference = this.countdownDate - now;

    if (difference > 0) {
      // Calcul du temps restant
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      // Mise à jour de l'HTML
      this.querySelector(
        ".edito-counter__counter-item--days .edito-counter__counter-value"
      ).textContent = days;
      this.querySelector(
        ".edito-counter__counter-item--hours .edito-counter__counter-value"
      ).textContent = hours;
      this.querySelector(
        ".edito-counter__counter-item--minutes .edito-counter__counter-value"
      ).textContent = minutes;
      this.querySelector(
        ".edito-counter__counter-item--seconds .edito-counter__counter-value"
      ).textContent = seconds;
    } else {
      // Le compte à rebours est terminé
      clearInterval(this.interval);
    }
  }

  setCounter() {
    this.updateCountdown();
    // Mise à jour du compte à rebours toutes les secondes
    this.interval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  }
}

customElements.define("edito-counter", EditoCounter);

document.querySelectorAll(".ProductForm__Variants input")?.forEach((input) => {
  input.addEventListener("change", () => {
    setTimeout(() => {
      const basePrice = document.querySelector(
        ".ProductMeta__PriceList .ProductMeta__Price"
      ).textContent;
      const basePriceValue = parseFloat(
        basePrice.replace(/[^0-9,]/g, "").replace(",", ".")
      );
      const comparePrice = Math.round(basePriceValue + 10);

      const compareAtPriceElement = document.getElementById(
        "preorder-compare-at-price"
      );
      const priceElement = document.getElementById("preoder-price");

      if (compareAtPriceElement)
        compareAtPriceElement.textContent = comparePrice + "€";
      if (priceElement) priceElement.textContent = basePrice;
    }, 10);
  });
});